import React, { useCallback, useEffect, useRef, useState } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { AnalyticsContext } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Text } from '@atlaskit/primitives/compiled';
import UFOCustomCohortData from '@atlaskit/react-ufo/custom-cohort-data';
import UFOCustomData from '@atlaskit/react-ufo/custom-data';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { token } from '@atlaskit/tokens';
import { getFrontendExperiment } from '@atlassian/search-experiment';

import { AIAnswerError } from '../../ai-answer-dialog/ai-answer-body/ai-answer-result/ai-answer-error';
import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { SmartAnswerFooterSection } from '../../ai-answer-dialog/ai-answer-footer/ai-answer-footer';
import {
	aiAnswerCollapseClicked,
	getSmartLinksAnalyticsContextData,
	onAIAnswerResultCompleteShown,
	onAIAnswerResultFirstByteShown,
	onAIAnswerSourceClick,
	SmartLinksAnalyticsContextLocation,
} from '../../ai-answer-dialog/analytics';
import { type NLPSearchResultSource } from '../../ai-answer-dialog/types';
import {
	SainAction,
	useSAINUsageCriteriaMet,
} from '../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../common/controller/analytics/use-analytics';
import { QueryIntent } from '../../constants';
import { DynamicLayout } from '../../dynamic-layout/dynamic-layout';
import { useBufferedContent } from '../common/hooks/use-buffered-content';
import { useContentTruncationDetector } from '../common/hooks/use-content-truncation-detector';
import { useDelayedResizeObserver } from '../common/hooks/use-delayed-resize-observer';
import { AICollapse } from '../common/ui/ai-collapse';
import { AIDisclaimerAndFeedback } from '../common/ui/ai-disclaimer-and-feedback';
import { SingleLineAnimatedWrapper } from '../common/ui/single-line-animated-wrapper';
import { SmartAnswerCardBodyGenerating } from '../common/ui/smart-answer-card-body-generating';

import { messages } from './messages';

const styles = cssMap({
	// Force the smart answer card to be the full max width of
	// the search page layout body main content container
	fullWidth: {
		width: '100vw',
		maxWidth: '100%',
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
	},
	container: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
	},
	interactive: {
		cursor: 'pointer',
		backgroundColor: token('color.background.neutral.subtle'),
		width: '100%',
	},
	expanded: {
		backgroundColor: token('color.background.neutral.subtle'),
		width: '100%',
	},
	root: {
		minHeight: '198px',
	},
	explicitQuestion: {
		minHeight: '350px',
	},
	fullHeight: {
		height: '100%',
	},
	disclaimer: {
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingTop: token('space.150'),
	},
});

type SmartAnswerCardDynamicLayoutProps = {
	onFMPReached?: () => void;
	onAnswerExpandedChange?: (expanded: boolean) => void;
	queryIntent?: QueryIntent;
};

function SmartAnswerCardDynamicLayoutComponent({
	onFMPReached,
	onAnswerExpandedChange,
	queryIntent,
}: SmartAnswerCardDynamicLayoutProps) {
	const [collapsed, setCollapsed] = useState(true);
	const [didUserTriggerCollapse, setDidUserTriggerCollapse] = useState(false);
	const [isVisibleTextComplete, setIsVisibleTextComplete] = useState(false);
	const [showAnimation, setShowAnimation] = useState(false);

	const componentRenderTimeRef = useRef<number>(Date.now());
	const firstByteEventFiredRef = useRef(false);

	const { commonAttributes, hasError, isAnswered, loading, partialResponse } = useAIAnswerContext();
	const { queryHash, searchSessionId, answerString, sources, errorStateSuggestedQueries } =
		commonAttributes;
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const streamingText = partialResponse?.partialNlpResult ?? '';
	const [streamingTextToDisplay, setStreamingTextToDisplay] = useState<string>(streamingText);

	// Refs for content truncation detection
	const isVisibleTextCompleteRef = useRef(false);
	const completeAnswerAnalyticsEventFiredRef = useRef(false);
	const renderedContentRef = useRef<HTMLDivElement>(null);
	const contentContainerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (collapsed && !isVisibleTextComplete) {
			setStreamingTextToDisplay(streamingText);
		} else if (!collapsed) {
			setStreamingTextToDisplay(streamingText);
		}
	}, [collapsed, isVisibleTextComplete, streamingText]);

	// Notify context when collapsed state changes
	useEffect(() => {
		onAnswerExpandedChange?.(!collapsed);
	}, [collapsed, onAnswerExpandedChange]);

	useEffect(() => {
		if (!loading && !hasError && !isVisibleTextComplete) {
			setIsVisibleTextComplete(true);
			onFMPReached?.(); // Mark FMP as reached for cached answers
		}
	}, [loading, hasError, isVisibleTextComplete, onFMPReached]);

	// Track if we should show animations (if loading or partialResponse are true)
	useEffect(() => {
		if (!showAnimation && loading && partialResponse) {
			setShowAnimation(true);
		}
	}, [loading, partialResponse, showAnimation]); // use useEffect to trigger analytics event in order to avoid needing to
	// pass commonAttributes as a dependency to handleExpandCollapseButtonClick
	// and thus avoid re-rendering SmartAnswerCardBodyStreaming with every
	// update to commonAttributes (which gets updated with the streamed text)
	useEffect(() => {
		if (didUserTriggerCollapse) {
			fireAnalyticsEvent(aiAnswerCollapseClicked(commonAttributes));
		}
	}, [didUserTriggerCollapse, commonAttributes, fireAnalyticsEvent]);

	// Detect if content is truncated when collapsed
	const isContentTruncated = useContentTruncationDetector(
		renderedContentRef,
		contentContainerRef,
		collapsed,
	);

	// Complete answer analytics
	useEffect(() => {
		// Only fire once and ensure DOM element is ready with meaningful content
		if (completeAnswerAnalyticsEventFiredRef.current || !renderedContentRef.current) {
			return;
		}
		const answerHeight = renderedContentRef.current.getBoundingClientRect().height;
		const containerHeight = contentContainerRef.current?.getBoundingClientRect().height ?? 0;
		const whitespaceHeight = Math.max(containerHeight - answerHeight, 0);

		// Only fire if we have meaningful height (content is rendered)
		if (answerHeight > 0 && containerHeight > 0) {
			fireAnalyticsEvent(
				onAIAnswerResultCompleteShown({
					...commonAttributes,
					answerHeight,
					containerHeight,
					whitespaceHeight,
					queryIntent: queryIntent,
				}),
			);
			completeAnswerAnalyticsEventFiredRef.current = true;
		}
	}, [fireAnalyticsEvent, commonAttributes, queryIntent, isContentTruncated]);

	// SAIN usage event
	useEffect(() => {
		fireSAINUsageEvent({ commonAttributes });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleExpandCollapseButtonClick = useCallback(() => {
		if (collapsed) {
			setCollapsed(false);
			setDidUserTriggerCollapse(false);
		} else {
			setCollapsed(true);
			setDidUserTriggerCollapse(true);
		}
	}, [collapsed]);

	const handleVisibleTextComplete = useCallback(() => {
		setIsVisibleTextComplete(true);
		onFMPReached?.(); // Mark FMP as reached for streaming answers
	}, [onFMPReached]);

	const sourceClickCallback = useCallback(
		(index: number, source: NLPSearchResultSource, hostname?: string) => {
			fireAnalyticsEvent(
				onAIAnswerSourceClick({
					sourceIndex: index,
					hostname,
					sourceProduct: source.product || '',
					...commonAttributes,
				}),
			);
			fireSAINUsageEvent({
				metCriteria: SainAction.SOURCE_CLICKED,
				commonAttributes,
				sourceProduct: source.product || '',
			});
		},
		[fireAnalyticsEvent, commonAttributes, fireSAINUsageEvent],
	);

	// UFO resize observer for streaming
	const resizeObserverCallback = useCallback(() => {
		if (
			!isVisibleTextCompleteRef.current &&
			renderedContentRef.current &&
			contentContainerRef.current
		) {
			const renderedContentRefHeight = renderedContentRef.current.getBoundingClientRect().height;
			const contentContainerRefHeight = contentContainerRef.current.getBoundingClientRect().height;
			const isVisibleTextComplete = renderedContentRefHeight > contentContainerRefHeight;
			if (isVisibleTextComplete) {
				handleVisibleTextComplete();
				isVisibleTextCompleteRef.current = true;
			}
		}
	}, [handleVisibleTextComplete]);
	// eslint-disable-next-line react-hooks/rules-of-hooks
	useDelayedResizeObserver(renderedContentRef, resizeObserverCallback);

	const bufferedContent = useBufferedContent(streamingTextToDisplay, isAnswered && !loading);

	useEffect(() => {
		if (bufferedContent.length > 0 && !firstByteEventFiredRef.current) {
			fireAnalyticsEvent(
				onAIAnswerResultFirstByteShown({
					...commonAttributes,
					timeToFirstByte: Date.now() - componentRenderTimeRef.current,
				}),
			);
			firstByteEventFiredRef.current = true;
		}
	}, [bufferedContent, commonAttributes, fireAnalyticsEvent]);

	// Use answerString when complete, bufferedContent when streaming
	const contentToRender = isAnswered ? answerString : bufferedContent;

	// Experiments
	const { serveExperiment: serveErrorStateSuggestedQueriesExp } = getFrontendExperiment({
		abTestId: 'kd_sain_show_no_answer_suggestions_exp',
	});

	const showReadMoreButton = collapsed && isContentTruncated;

	// Single return statement with React component for main content
	const isStreaming = !isAnswered && (loading || loading === undefined);

	// Early return for error state
	if (hasError) {
		return (
			<Box xcss={styles.fullWidth}>
				<AIAnswerError />
			</Box>
		);
	}

	// Early return for generating state
	if (!isAnswered && (loading || loading === undefined)) {
		if (!partialResponse || bufferedContent.length < 1) {
			return (
				<Box xcss={styles.fullWidth}>
					<SingleLineAnimatedWrapper>
						<SmartAnswerCardBodyGenerating
							queryIntent={
								queryIntent === QueryIntent.NATURAL_LANGUAGE_QUERY
									? QueryIntent.EXPLICIT_QUESTION
									: queryIntent
							}
							heightOverride="lumina"
						/>
					</SingleLineAnimatedWrapper>
				</Box>
			);
		}
	}

	return (
		<Box xcss={styles.fullWidth}>
			<UFOSegment name="search-ai-dialog-all-text">
				<UFOSegment name="search-ai-dialog-visible-text">
					{isVisibleTextComplete ? null : (
						<UFOLoadHold name="search-ai-dialog-visible-text-loading" />
					)}
					{fg('platform_ufo_custom_cohort_data_migration') ? (
						<>
							<UFOCustomCohortData dataKey="queryHash" value={queryHash} />
							<UFOCustomCohortData dataKey="searchSessionId" value={searchSessionId} />
							<UFOCustomCohortData dataKey="queryIntent" value={queryIntent} />
						</>
					) : (
						<UFOCustomData
							data={{
								queryHash,
								searchSessionId,
								queryIntent,
							}}
						/>
					)}
					{/* Main content */}
					<SmartAnswerCardContent
						isStreaming={isStreaming}
						collapsed={collapsed}
						isContentTruncated={isContentTruncated}
						queryIntent={queryIntent}
						showReadMoreButton={showReadMoreButton}
						handleExpandCollapseButtonClick={handleExpandCollapseButtonClick}
						contentContainerRef={contentContainerRef}
						renderedContentRef={renderedContentRef}
						sources={sources}
						searchSessionId={searchSessionId}
						sourceClickCallback={sourceClickCallback}
						errorStateSuggestedQueries={errorStateSuggestedQueries}
						serveErrorStateSuggestedQueriesExp={serveErrorStateSuggestedQueriesExp}
						commonAttributes={commonAttributes}
						contentToRender={contentToRender}
						showAnimation={showAnimation}
					/>
				</UFOSegment>
			</UFOSegment>
		</Box>
	);
}

export function SmartAnswerCardContent({
	isStreaming,
	collapsed,
	isContentTruncated,
	queryIntent,
	showReadMoreButton,
	handleExpandCollapseButtonClick,
	contentContainerRef,
	renderedContentRef,
	errorStateSuggestedQueries,
	serveErrorStateSuggestedQueriesExp,
	commonAttributes,
	contentToRender,
}: {
	isStreaming: boolean;
	collapsed: boolean;
	isContentTruncated: boolean;
	queryIntent?: QueryIntent;
	showReadMoreButton: boolean;
	handleExpandCollapseButtonClick: () => void;
	contentContainerRef: React.RefObject<HTMLDivElement>;
	renderedContentRef: React.RefObject<HTMLDivElement>;
	sources: NLPSearchResultSource[];
	searchSessionId: string;
	sourceClickCallback: (index: number, source: NLPSearchResultSource, hostname?: string) => void;
	errorStateSuggestedQueries: string[];
	serveErrorStateSuggestedQueriesExp: boolean;
	commonAttributes: any;
	contentToRender: string;
	showAnimation: boolean;
}) {
	return (
		<AICollapse
			title={
				<Heading as="h2" size="xxsmall">
					<Text color="color.text.subtlest" size="small" weight="bold">
						<FormattedMessage {...messages.smart_answer_card_title} />
					</Text>
				</Heading>
			}
			isCollapsed={collapsed && (isStreaming || isContentTruncated)}
			onExpandCollapse={handleExpandCollapseButtonClick}
			ref={contentContainerRef}
			showReadMoreButton={showReadMoreButton}
			queryIntent={
				queryIntent === QueryIntent.NATURAL_LANGUAGE_QUERY
					? QueryIntent.EXPLICIT_QUESTION
					: queryIntent
			}
			hideExpandCollapseButton
		>
			<Box ref={renderedContentRef} testId="sain-fmp-target">
				<AnalyticsContext
					data={getSmartLinksAnalyticsContextData({
						location: SmartLinksAnalyticsContextLocation.SAIN_Q_AND_A_CARD,
						commonAttributes,
					})}
				>
					<SmartCardProvider>
						<Box>
							<DynamicLayout contentToRender={contentToRender} />
						</Box>
					</SmartCardProvider>
				</AnalyticsContext>
			</Box>

			{/* Disclaimer section */}
			{!isStreaming && !collapsed && (
				<Box xcss={styles.disclaimer}>
					<AIDisclaimerAndFeedback
						showFeedbackButtons={!isStreaming}
						showCopyButton={!isStreaming}
						renderedContentRef={renderedContentRef}
					/>
				</Box>
			)}

			{/* Footer section - only show when complete */}
			{!isStreaming &&
			(!collapsed ||
				(errorStateSuggestedQueries.length > 0 && serveErrorStateSuggestedQueriesExp)) ? (
				<SmartAnswerFooterSection showOpenChat={true} />
			) : null}
		</AICollapse>
	);
}

export function SmartAnswerCardDynamicLayout({
	onFMPReached,
	onAnswerExpandedChange,
	queryIntent,
}: SmartAnswerCardDynamicLayoutProps): React.JSX.Element {
	return (
		<UFOSegment name="smart-answer-card">
			<SmartAnswerCardDynamicLayoutComponent
				onFMPReached={onFMPReached}
				onAnswerExpandedChange={onAnswerExpandedChange}
				queryIntent={queryIntent}
			/>
		</UFOSegment>
	);
}
